/**
 * Row-count truth for transaction loads (NS-36).
 *
 * The /transactions response says how many rows it carried (`fetched_count`), the wallet's history
 * length only when that is a real count (`total`, otherwise null), whether more may exist
 * (`has_more`: true unless the fetcher reported the history exhausted), and whether the result is
 * cut or short (`truncated`, `incomplete`, `hub_capped`). These helpers turn that into node data
 * and into the wording the Inspector shows, so that nothing is called "all loaded" or "N of M"
 * on a guess.
 */

/** Rows added by the Transfers tab "Load Next 100" button. */
export const NEXT_TX_BATCH = 100;

export interface TxPageResponse {
    transactions?: unknown[];
    fetched_count?: number;
    total?: number | null;
    has_more?: boolean;
    truncated?: boolean;
    incomplete?: boolean;
    hub_capped?: boolean;
}

/** Coverage fields kept on the node beside `tx_count` (the triage count). */
export interface TxCoverageFields {
    tx_total_real?: number | null;
    tx_has_more?: boolean;
    tx_truncated?: boolean;
    tx_incomplete?: boolean;
}

/**
 * The wallet's history length when the response carries a real one, else null.
 * A response without `fetched_count` predates the row-count fix: its `total` is the page size and
 * is not a count of the wallet, so it is ignored.
 */
export function realTotal(response: TxPageResponse | null | undefined): number | null {
    if (!response || response.fetched_count === undefined) return null;
    const t = response.total;
    return typeof t === 'number' && Number.isFinite(t) && t >= 0 ? t : null;
}

/**
 * Node fields after a load. `tx_count` becomes the real total when there is one and otherwise
 * keeps what it was (the triage count) - it is never set to the page size.
 */
export function coverageAfterLoad(
    response: TxPageResponse | null | undefined,
    prev: { tx_count?: number; tx_total_real?: number | null } | null | undefined,
): { tx_count: number } & TxCoverageFields {
    const real = realTotal(response);
    const known = response && response.fetched_count !== undefined;
    return {
        tx_count: real ?? prev?.tx_count ?? 0,
        tx_total_real: real ?? prev?.tx_total_real ?? null,
        tx_has_more: known ? response!.has_more !== false : undefined,
        tx_truncated: known ? response!.truncated === true : undefined,
        tx_incomplete: known ? response!.incomplete === true : undefined,
    };
}

/**
 * What the bar says about how much is loaded.
 *   real M above N  -> "N of M"
 *   has_more false  -> "N loaded, complete"
 *   has_more true   -> "N loaded, more available"
 *   unknown         -> "N loaded"
 */
export function loadedSummary(loaded: number, total: number | null | undefined, hasMore: boolean | undefined): string {
    if (typeof total === 'number' && total > loaded) return `${loaded} of ${total.toLocaleString()}`;
    if (hasMore === false) return `${loaded} loaded, complete`;
    if (hasMore === true) return `${loaded} loaded, more available`;
    return `${loaded} loaded`;
}

/** The "partial" chip shows when the result was cut or a leg of the read failed. */
export function isPartial(f: { truncated?: boolean; incomplete?: boolean } | null | undefined): boolean {
    return !!f && (f.truncated === true || f.incomplete === true);
}

export function partialReason(f: { truncated?: boolean; incomplete?: boolean } | null | undefined): string {
    if (!f) return '';
    if (f.incomplete) return 'A part of the read failed, so these rows may be a prefix of the history. Reload to retry.';
    if (f.truncated) return 'The result was cut by a cap (hub limit or page limit); older rows exist.';
    return '';
}

/**
 * A short batch is "all loaded" only when the fetcher said the history ended. `has_more` absent
 * (an older server) is treated as not exhausted.
 */
export function isExhausted(response: TxPageResponse | null | undefined): boolean {
    return !!response && response.has_more === false && response.incomplete !== true;
}

/** The hub notice fires from the backend flag, or from the frontend pre-clamp. */
export function isHubCapNotice(
    response: TxPageResponse | null | undefined,
    usedLimit: number,
    requestedLimit: number,
): boolean {
    return response?.hub_capped === true || usedLimit < requestedLimit;
}
